import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";

const PALETTE = {
  primaryRed: "#D84E55",
  accentBlue: "#3A86FF",
  border: "#E5E7EB",
};

const Container = ({ children }) => (
  <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">{children}</div>
);

const Badge = ({ children }) => (
  <span className="inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold bg-white">
    {children}
  </span>
);

const SectionCard = ({ id, title, children }) => (
  <section id={id} className="bg-white border border-gray-200 rounded-2xl p-5 sm:p-7 shadow-sm">
    <h2 className="text-lg sm:text-xl font-semibold text-gray-900">{title}</h2>
    <div className="mt-3 text-[15px] leading-7 text-gray-700">{children}</div>
  </section>
);

const Terms = () => {
  const lastUpdated = useMemo(() => "5 Apr 2026", []);

  useEffect(() => {
    document.title = "Terms & Conditions | Routesbook.lk";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <div
        className="w-full border-b"
        style={{
          borderColor: PALETTE.border,
          background:
            "linear-gradient(135deg, rgba(216,78,85,0.10) 0%, rgba(58,134,255,0.10) 100%)",
        }}
      >
        <Container>
          <div className="py-10 sm:py-12">
            <div className="flex items-center gap-3 text-sm">
              <Link to="/" className="text-blue-700 hover:underline">
                Home
              </Link>
              <span className="text-gray-400">/</span>
              <span className="text-gray-700">Terms & Conditions</span>
            </div>

            <div className="mt-4 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
              <div>
                <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-gray-900">
                  Terms & Conditions
                </h1>
                <p className="mt-2 text-sm sm:text-base text-gray-600">
                  These terms govern your use of Routesbook.lk and its booking services.
                </p>
              </div>

              <div className="flex items-center gap-2">
                <Badge>Last updated: {lastUpdated}</Badge>
              </div>
            </div>
          </div>
        </Container>
      </div>

      <Container>
        {/* Quick action */}
        <div className="mt-8 bg-white border border-gray-200 rounded-2xl p-5 sm:p-6 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-sm text-gray-700">
              Also read our{" "}
              <Link to="/privacy-policy" className="font-semibold hover:underline" style={{ color: PALETTE.accentBlue }}>
                Privacy Policy
              </Link>{" "}
              and{" "}
              <Link to="/refund-policy" className="font-semibold hover:underline" style={{ color: PALETTE.accentBlue }}>
                Refund Policy
              </Link>.
            </p>
            <Link
              to="/refund-policy"
              className="inline-flex items-center justify-center rounded-xl px-4 py-2 text-sm font-semibold text-white"
              style={{ backgroundColor: PALETTE.primaryRed }}
            >
              Refund Policy
            </Link>
          </div>
        </div>

        {/* TOC */}
        <div className="mt-6 bg-white border border-gray-200 rounded-2xl p-5 sm:p-6 shadow-sm">
          <div className="text-sm font-semibold text-gray-900">On this page</div>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            {[
              ["about", "About Routesbook.lk"],
              ["accounts", "Accounts & eligibility"],
              ["bookings", "Bookings & tickets"],
              ["changes", "Schedule changes & cancellations"],
              ["payments", "Payments"],
              ["conduct", "User conduct"],
              ["liability", "Liability & disclaimers"],
              ["termination", "Account suspension & termination"],
              ["updates", "Changes to terms"],
              ["law", "Governing law"],
              ["contact", "Contact"],
            ].map(([id, label]) => (
              <a key={id} href={`#${id}`} className="hover:underline" style={{ color: PALETTE.accentBlue }}>
                {label}
              </a>
            ))}
          </div>
        </div>

        <div className="mt-6 space-y-5 pb-16">
          <SectionCard id="about" title="1) About Routesbook.lk">
            <p>
              Routesbook.lk is an online platform operated by <b>NDD IT Solutions</b> that enables users to search buses,
              view seat availability (where supported), and book tickets provided by third-party bus operators.
            </p>
            <p className="mt-3">
              Routesbook.lk acts solely as a booking intermediary. The transport service is provided directly by the bus
              operator. Routesbook.lk is not responsible for the actual execution of the trip.
            </p>
          </SectionCard>

          <SectionCard id="accounts" title="2) Accounts & eligibility">
            <ul className="list-disc pl-5 space-y-2">
              <li>You must provide accurate and complete information when creating an account or booking.</li>
              <li>You are responsible for maintaining the confidentiality of your account.</li>
              <li>You are responsible for all activities under your account.</li>
            </ul>
          </SectionCard>

          <SectionCard id="bookings" title="3) Bookings & tickets">
            <ul className="list-disc pl-5 space-y-2">
              <li>Seat availability and pricing are subject to real-time changes.</li>
              <li>A booking is confirmed only after successful payment.</li>
              <li>Routesbook.lk may cancel bookings in case of system errors or incorrect pricing.</li>
              <li>Passengers must carry valid identification if required by the operator.</li>
            </ul>
          </SectionCard>

          <SectionCard id="changes" title="4) Schedule changes & cancellations">
            <ul className="list-disc pl-5 space-y-2">
              <li>Operators may change schedules, boarding points, or cancel trips.</li>
              <li>Refunds will follow the Refund Policy.</li>
              <li>Routesbook.lk is not responsible for operator delays or disruptions.</li>
            </ul>
          </SectionCard>

          <SectionCard id="payments" title="5) Payments">
            <ul className="list-disc pl-5 space-y-2">
              <li>Payments are processed via PayHere or other payment providers.</li>
              <li>We do not store card details.</li>
              <li>Prices may include service fees.</li>
              <li>In case of payment errors, bookings may be cancelled or corrected.</li>
            </ul>
          </SectionCard>

          <SectionCard id="conduct" title="6) User conduct">
            <ul className="list-disc pl-5 space-y-2">
              <li>Do not misuse or attempt to hack the system.</li>
              <li>Do not perform fraudulent chargebacks.</li>
            </ul>
          </SectionCard>

          <SectionCard id="liability" title="7) Liability & disclaimers">
            <p>
              The platform is provided "as is". Routesbook.lk does not guarantee uninterrupted service.
              We are not responsible for delays, cancellations, accidents, traffic conditions, or events beyond our control.
            </p>
          </SectionCard>

          <SectionCard id="termination" title="8) Account suspension & termination">
            <p>
              We reserve the right to suspend or terminate accounts without notice if misuse, fraud, or violations are detected.
            </p>
          </SectionCard>

          <SectionCard id="updates" title="9) Changes to terms">
            <p>
              We may update these terms at any time. Continued use of the platform means acceptance of updated terms.
            </p>
          </SectionCard>

          <SectionCard id="law" title="10) Governing law">
            <p>This agreement is governed by the laws of Sri Lanka.</p>
          </SectionCard>

          <SectionCard id="contact" title="11) Contact">
            <p>
              Email:{" "}
              <a className="font-semibold hover:underline" href="mailto:support@routesbook.lk" style={{ color: PALETTE.accentBlue }}>
                support@routesbook.lk
              </a>
            </p>
            <p className="mt-2">
              Business name: <b>NDD IT Solutions</b>
              <br />
              Address: <b>Lenaduwa Junction, Godagama, Palatuwa, Matara, Sri Lanka</b>
              <br />
              Phone: <b>0767037547</b>
            </p>
          </SectionCard>

          <div className="text-xs text-gray-500">
            This page is provided for general informational purposes and does not constitute legal advice.
          </div>
        </div>
      </Container>
    </div>
  );
};

export default Terms;
